import {
  ButtonGroup,
} from '@chakra-ui/react';
import React, {
  useEffect, useState, Dispatch, SetStateAction,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DateInput from '@app/components/chakra/date-input';
import { templatesSlice } from '@app/slices/templates';
import { Button } from '@chakra-snippets/button';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogTitle,
} from '@chakra-snippets/dialog';
import { Field } from '@chakra-snippets/field';
import './ScheduleMessage.less';

const MAX_DAYS = 90;

type ScheduleMessageProps = {
  chatId: string;
  jobId: string | number;
  queueMessage: (message: unknown) => void;
  show: boolean;
  toggleShow: (value: boolean) => void;
  scheduledMessageText: string;
  setMessageValue: Dispatch<SetStateAction<string>>;
};

const ScheduleMessage: React.FC<ScheduleMessageProps> = ({
  chatId,
  jobId,
  queueMessage,
  show,
  toggleShow,
  scheduledMessageText,
  setMessageValue,
}) => {
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');
  const { clearLoadedTemplate } = templatesSlice.actions;
  const [scheduledMsgText, setScheduledMsgText] = useState(scheduledMessageText);

  // close this modal and reset date and time
  const closeAndReset = () => {
    toggleShow(false);
    setScheduledDateTime('');
  };
  const dispatch = useDispatch();

  const isDisabled = !setScheduledDateTime || !scheduledMsgText;

  const handleSubmit = () => {
    const formObject = {
      message: scheduledMsgText,
      eventTime: new Date(scheduledDateTime).getTime(),
    };
    queueMessage({
      ...formObject,
      chatId,
      jobId,
    });
    closeAndReset();
    setMessageValue('');
  };

  const { context, loadedTemplateText } = useSelector(({ ui, templates }) => ({
    context: ui.context,
    loadedTemplateText: templates.loadedTemplateText,
  }));

  useEffect(() => {
    if (context === 'SCHEDULED_MESSAGE' && loadedTemplateText) {
      setScheduledMsgText(loadedTemplateText);
      dispatch(clearLoadedTemplate());
    }
  }, [loadedTemplateText, context, dispatch, clearLoadedTemplate]);

  return (
    <DialogRoot placement="center" open={show} onExitComplete={closeAndReset} data-testId="schedule-message-header">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schedule Message</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="Date">
            <DateInput
              id="scheduledDateTime"
              selectedDate={scheduledDateTime ? new Date(scheduledDateTime) : null}
              handleDateChange={(date) => setScheduledDateTime(date?.toISOString() || '')}
              includeTime
              dateLimit="future"
              placeholderText="Select date and time"
              minDate={new Date()}
              maxDate={new Date(Date.now() + MAX_DAYS * 24 * 60 * 60 * 1000)}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <ButtonGroup>
            <Button variant="outline" onClick={closeAndReset}>
              Cancel
            </Button>
            <Button disabled={isDisabled} onClick={handleSubmit}>
              Schedule Message
            </Button>
          </ButtonGroup>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default ScheduleMessage;
